
import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export type WorkflowConfig = {
  analytics?: {
    schedule?: string;
    time?: string;
  };
  reports?: {
    schedule?: string;
    time?: string;
  };
  workflow?: {
    schedule?: string;
    time?: string;
    days?: string[];
  };
  notifications?: {
    email?: boolean;
  };
  dataSources?: {
    crmTypes?: string[];
    storagePreference?: string;
    sortBy?: string;
    cleaningPreference?: string;
    automaticModeling?: boolean;
  };
};

export const useWorkflow = () => {
  const [lastError, setLastError] = useState<Error | null>(null);

  // Fetch workflows with improved error handling to avoid team recursion
  const { data: workflows, isLoading, refetch, error } = useQuery({
    queryKey: ['workflows'],
    queryFn: async () => {
      console.log('Fetching workflows...');
      try {
        // First check if user is authenticated
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!session) {
          console.log('No active session found, returning empty workflows array');
          return [];
        }
        
        // Fetch workflows directly without team joins to avoid recursion
        const { data, error } = await supabase
          .from('workflows')
          .select('id, name, config, status, created_at, updated_at, created_by, schedule')
          .eq('created_by', session.user.id)
          .is('team_id', null) // Only get personal workflows to avoid team recursion
          .order('created_at', { ascending: false })
          .limit(10);

        if (error) {
          console.error('Error fetching workflows:', error);
          setLastError(new Error(error.message));
          return [];
        }

        console.log('Workflows fetched successfully:', data?.length || 0);
        return data?.map(workflow => ({
          ...workflow,
          config: typeof workflow.config === 'string' 
            ? JSON.parse(workflow.config) 
            : workflow.config
        })) || [];
      } catch (error) {
        console.error('Error fetching workflows:', error);
        setLastError(error instanceof Error ? error : new Error('Failed to load workflows'));
        return [];
      }
    },
    retry: (failureCount, error) => {
      // Don't retry on policy errors
      if (error instanceof Error && 
          (error.message.includes("recursion") || error.message.includes("permission"))) {
        return false;
      }
      return failureCount < 2;
    },
    staleTime: 30000, // 30 seconds
  });

  // Update workflow configuration with improved error handling
  const { mutate: updateWorkflow, isPending } = useMutation({
    mutationFn: async (config: WorkflowConfig) => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!session) {
          throw new Error('User not authenticated');
        }

        console.log('Updating workflow with config:', config);

        if (workflows && workflows.length > 0) {
          // Update existing workflow
          const { data, error } = await supabase
            .from('workflows')
            .update({
              config,
              status: 'active',
              updated_at: new Date().toISOString()
            })
            .eq('id', workflows[0].id)
            .eq('created_by', session.user.id) // Extra security check
            .select();

          if (error) {
            console.error('Error updating workflow:', error);
            throw error;
          }

          console.log('Workflow updated successfully:', data);
          return data;
        } else {
          // Create new workflow - ensure no team_id to avoid recursion
          const { data, error } = await supabase
            .from('workflows')
            .insert({
              name: 'Scheduled Tasks',
              config,
              status: 'active',
              created_by: session.user.id,
              team_id: null // Explicitly set to null to avoid team policies
            })
            .select();

          if (error) {
            console.error('Error creating workflow:', error);
            throw error;
          }

          console.log('Workflow created successfully:', data);
          return data;
        }
      } catch (error) {
        console.error('Error in mutation:', error);
        throw error;
      }
    },
    onSuccess: () => {
      console.log('Workflow operation successful');
      toast.success('Scheduled tasks saved successfully');
      refetch();
    },
    onError: (error) => {
      console.error('Error updating workflow:', error);
      setLastError(error instanceof Error ? error : new Error('Failed to update workflow'));
      toast.error('Failed to save scheduled tasks');
    }
  });

  return {
    workflows,
    isLoading,
    updateWorkflow,
    isPending,
    error: lastError || error
  };
};
